// Account: how to connect Claude, ChatGPT or Claude Code to SMART (Gio, 2026-11-02). The steps
// mirror docs/mcp.md *How people connect*; the menu names are each app's own, read from
// support.claude.com (custom connectors) and developers.openai.com/plugins (connect-chatgpt),
// 2026-11-02. Open by default until the person has connected an app.

import { useEffect, useState, type ReactNode } from 'react';
import { Icons } from '@/components/primitives';
import { CONNECTOR_FIRST_PROMPT, claudeCodeCommand } from '@/lib/credentials';

const STEP_LIST = { margin: 0, paddingLeft: 18, display: 'grid', gap: 6, fontSize: 12.5, lineHeight: 1.5 } as const;
const APP_TITLE = { margin: '0 0 6px', fontSize: 13, fontWeight: 650, color: 'var(--ink)' } as const;
const APP_NOTE = { margin: '8px 0 0', fontSize: 11.5, lineHeight: 1.5, color: 'var(--ink-2)' } as const;

const CODE_BLOCK = {
  flex: '1 1 0', minWidth: 0, margin: 0, fontSize: 11.5, lineHeight: 1.5, padding: '6px 10px',
  background: 'var(--bg-sunk)', border: '1px solid var(--line)', borderRadius: 4,
  overflowWrap: 'anywhere', whiteSpace: 'pre-wrap', color: 'var(--ink)',
} as const;

/** A small code block with a Copy button beside it (no button where the page has no clipboard). */
function CopyBlock({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);
  const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
  return (
    <div style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
      <code className="mono" style={CODE_BLOCK}>{value}</code>
      {clipboard && (
        <button
          type="button"
          className="btn btn-sm"
          aria-label={`Copy ${label}`}
          style={{ minHeight: 32, flexShrink: 0 }}
          onClick={() => {
            void clipboard.writeText(value).then(() => setCopied(true), () => {});
          }}
        >
          {Icons.copy} {copied ? 'Copied' : 'Copy'}
        </button>
      )}
    </div>
  );
}

function App({ title, plans, children }: { title: string; plans: string; children: ReactNode }) {
  return (
    <section aria-label={title} style={{ minWidth: 0 }}>
      <h3 style={APP_TITLE}>
        {title}
        <span className="muted" style={{ fontWeight: 400, fontSize: 11.5 }}> · {plans}</span>
      </h3>
      {children}
    </section>
  );
}

interface ConnectHelpProps {
  /** The connector's address (`connectorUrl`). */
  connector: string;
  apiKeysEnabled: boolean;
  /** Open on arrival: the person has no connected app yet. */
  defaultOpen: boolean;
}

export function ConnectHelp({ connector, apiKeysEnabled, defaultOpen }: ConnectHelpProps) {
  return (
    <details className="panel" open={defaultOpen} style={{ marginBottom: 14 }}>
      <summary style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 650 }}>
        How to connect Claude, ChatGPT or Claude Code
      </summary>
      <div style={{ padding: '0 14px 14px', display: 'grid', gap: 14 }}>
        <div style={{ display: 'grid', gap: 6, fontSize: 12.5 }}>
          <span>Every app uses this address:</span>
          <CopyBlock value={connector} label="the connector address" />
        </div>
        <div style={{ display: 'grid', gap: 6, fontSize: 12.5 }}>
          <span>Once connected, start your first chat in Claude or ChatGPT with:</span>
          <CopyBlock value={CONNECTOR_FIRST_PROMPT} label="the first message" />
          <span className="muted" style={{ fontSize: 11.5, lineHeight: 1.5 }}>
            With the app’s memory on, “SMART” then means this connector in every chat that follows.
          </span>
        </div>

        <div className="connect-help-apps">
          <App title="Claude" plans="claude.ai, desktop and mobile">
            <ol style={STEP_LIST}>
              <li>
                In Claude, open <strong>Customize → Connectors</strong> and choose{' '}
                <strong>Add custom connector</strong>. Name it SMART, paste the address and leave
                the advanced settings empty.
              </li>
              <li>
                Press <strong>Connect</strong>. SMART opens: sign in, choose what Claude may do,
                and press <strong>Allow</strong>.
              </li>
              <li>In a chat, turn SMART on from the connectors menu and send the first message above.</li>
            </ol>
            <p style={APP_NOTE}>
              On a Team or Enterprise plan, an owner of your Claude organization adds it once
              under <strong>Organization settings → Connectors</strong> (Add → Custom → Web).
              You then find it under Customize → Connectors and press Connect.
            </p>
          </App>

          <App title="ChatGPT" plans="on the web, any plan">
            <ol style={STEP_LIST}>
              <li>
                On chatgpt.com, open <strong>Plugins → + → Add custom MCP server</strong>. Name it
                SMART, enter the address as a public endpoint with OAuth sign-in (leave the client
                fields empty), and create it.
              </li>
              <li>
                SMART opens: sign in, choose what ChatGPT may do, and press <strong>Allow</strong>.
              </li>
              <li>
                In a new chat, type <strong>@</strong>, pick SMART and send the first message above.
              </li>
            </ol>
            <p style={APP_NOTE}>
              On a Business, Enterprise or Edu workspace, an admin can add it once and publish it
              for everyone. ChatGPT then shows everyone the tools available to whoever signed in
              when it was added, so add it signed in as a SMART Admin who is not limited to
              particular productions, with every box ticked, and before publishing ask it “What
              can I do in SMART?”: it must say it may administer SMART, or reconnect first. Press{' '}
              <strong>Refresh</strong> on the plugin when SMART gains new tools.
            </p>
          </App>

          <App title="Claude Code" plans="with an API key">
            {apiKeysEnabled ? (
              <>
                <ol style={STEP_LIST}>
                  <li>Create an API key above and copy it.</li>
                  <li>
                    Run this in a terminal, with your key in place of &lt;key&gt;:
                    <div style={{ marginTop: 6 }}>
                      <CopyBlock value={claudeCodeCommand(connector)} label="the Claude Code command" />
                    </div>
                  </li>
                </ol>
                <p style={APP_NOTE}>
                  The key is a secret: Claude Code keeps it in its settings file, and your shell
                  may keep the line in its history.
                </p>
              </>
            ) : (
              <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5 }}>
                Claude Code connects with an API key, once an admin turns API keys on.
              </p>
            )}
          </App>
        </div>

        <p style={{ margin: 0, fontSize: 11.5, lineHeight: 1.5, color: 'var(--ink-2)' }}>
          Whatever you allow, the app acts as you and never beyond your role, and it can never
          launch or pause a campaign. Claude and ChatGPT are listed below once connected, where
          you can revoke them; Claude Code uses your API key, revoked under API keys above.
        </p>
      </div>
    </details>
  );
}
